import { z } from "zod";

const fieldsSchema = z.object({
  registration: z.string().max(16), vin: z.string().max(32),
  make: z.string().max(80), model: z.string().max(120), version: z.string().max(160),
  mileage: z.string().max(20), targetPrice: z.string().max(20),
});
const valuesSchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]));
const mutationSchema = z.object({
  method: z.enum(["POST", "PATCH"]), path: z.string(), key: z.string(),
  body: valuesSchema, startedAt: z.string().datetime(),
});
export const intakeDraftSchema = z.object({
  version: z.literal(1), fields: fieldsSchema, step: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  vehicleId: z.string().nullable(), creationRequested: z.boolean(),
  synced: valuesSchema, pending: mutationSchema.nullable(),
  updatedAt: z.string().datetime(),
});
export type IntakeFields = z.infer<typeof fieldsSchema>;
export type IntakeDraft = z.infer<typeof intakeDraftSchema>;
export type IntakeMutation = z.infer<typeof mutationSchema>;
type Values = z.infer<typeof valuesSchema>;
export type IntakeSaveStatus = "local" | "saving" | "saved" | "error";

export function emptyIntakeDraft(): IntakeDraft {
  return {
    version: 1, fields: { registration: "", vin: "", make: "", model: "", version: "", mileage: "", targetPrice: "" },
    step: 0, vehicleId: null, creationRequested: false, synced: {}, pending: null, updatedAt: new Date().toISOString(),
  };
}

export function intakeFieldErrors(fields: IntakeFields): Partial<Record<keyof IntakeFields, string>> {
  const errors: Partial<Record<keyof IntakeFields, string>> = {};
  if (fields.mileage && (!/^\d+$/.test(fields.mileage) || Number(fields.mileage) > 5_000_000)) {
    errors.mileage = "Indica kilómetros enteros entre 0 y 5.000.000.";
  }
  if (fields.targetPrice && (!/^\d+(?:[.,]\d{1,2})?$/.test(fields.targetPrice) || Number(fields.targetPrice.replace(",", ".")) > 10_000_000)) {
    errors.targetPrice = "Indica un precio sin separador de miles y con hasta dos decimales.";
  }
  return errors;
}

export function intakeMissingData(fields: IntakeFields): string[] {
  const errors = intakeFieldErrors(fields);
  return [
    !fields.registration.trim() && !fields.vin.trim() ? "Matrícula o bastidor" : null,
    !fields.make.trim() ? "Marca" : null,
    !fields.model.trim() ? "Modelo" : null,
    !fields.mileage || errors.mileage ? "Kilómetros" : null,
    !fields.targetPrice || errors.targetPrice ? "Precio de venta" : null,
  ].filter((value): value is string => value !== null);
}

export function intakeHasIdentity(fields: IntakeFields): boolean {
  return Boolean(fields.registration.trim() || fields.vin.trim() || (fields.make.trim() && fields.model.trim()));
}

function values(fields: IntakeFields): Values {
  const errors = intakeFieldErrors(fields);
  return {
    registration: fields.registration.trim().toUpperCase() || null,
    vin: fields.vin.trim().toUpperCase() || null,
    make: fields.make.trim() || null, model: fields.model.trim() || null, version: fields.version.trim() || null,
    ...(!errors.mileage ? { mileage: fields.mileage === "" ? null : Number(fields.mileage) } : {}),
    ...(!errors.targetPrice ? { targetPriceCents: fields.targetPrice === "" ? null : Math.round(Number(fields.targetPrice.replace(",", ".")) * 100) } : {}),
  };
}

interface IntakeIO {
  save(draft: IntakeDraft): Promise<void>;
  send(mutation: IntakeMutation): Promise<{ id: string }>;
  onChange?(): void;
}

/** Una sola escritura remota a la vez; la operación se guarda antes de enviarse.
 * Un corte de red recupera la MISMA clave y cuerpo, nunca crea otro vehículo.
 * La copia local está cifrada y aislada por usuario y concesionario en el adaptador.
 */
export class IntakeDraftSession {
  draft: IntakeDraft;
  status: IntakeSaveStatus;
  error: string | null = null;
  private localError = false;
  private localWrites = 0;
  private storage: Promise<void> = Promise.resolve();
  private running: Promise<boolean> | null = null;

  constructor(draft: IntakeDraft, private readonly io: IntakeIO) {
    this.draft = structuredClone(draft);
    const dirty = Object.entries(values(draft.fields)).some(([key, value]) => value !== (draft.synced[key] ?? null));
    const invalid = Object.keys(intakeFieldErrors(draft.fields)).length > 0;
    this.status = draft.vehicleId && !draft.pending && !dirty && !invalid ? "saved" : "local";
  }

  get savingLocally() { return this.localWrites > 0; }

  private notify() { this.io.onChange?.(); }
  private save() {
    this.draft = { ...this.draft, updatedAt: new Date().toISOString() };
    const snapshot = structuredClone(this.draft);
    this.localWrites += 1;
    this.storage = this.storage.then(async () => {
      try {
        await this.io.save(snapshot);
        this.localError = false;
      } catch {
        this.localError = true;
        this.status = "error";
        this.error = "No podemos guardar en este dispositivo. Mantén esta ventana abierta y vuelve a intentarlo.";
      }
      this.localWrites -= 1;
      this.notify();
    });
    this.notify();
  }

  edit(patch: Partial<IntakeFields>) {
    this.draft = { ...this.draft, fields: { ...this.draft.fields, ...patch } };
    this.status = "local";
    this.error = null;
    this.save();
  }

  step(step: IntakeDraft["step"]) { this.draft = { ...this.draft, step }; this.save(); }

  async persisted() {
    await this.storage;
    if (this.localError) throw new Error(this.error ?? "No se ha guardado el borrador.");
  }

  async settled() { await this.running; await this.persisted(); }

  flush(create = false): Promise<boolean> {
    if (create) {
      this.draft = { ...this.draft, creationRequested: true };
      this.save();
    } else if (this.localError) this.save();
    if (this.running) return this.running;
    this.running = this.sync().finally(() => { this.running = null; });
    return this.running;
  }

  private async sync(): Promise<boolean> {
    try {
      await this.persisted();
      for (;;) {
        if (!this.draft.pending) {
          const current = values(this.draft.fields);
          if (!this.draft.vehicleId && (!this.draft.creationRequested || !intakeHasIdentity(this.draft.fields))) {
            this.status = "local";
            this.notify();
            return false;
          }
          const body = this.draft.vehicleId
            ? Object.fromEntries(Object.entries(current).filter(([key, value]) => value !== (this.draft.synced[key] ?? null)))
            : { ...Object.fromEntries(Object.entries(current).filter(([, value]) => value !== null)), status: "draft", origin: "national" };
          if (this.draft.vehicleId && Object.keys(body).length === 0) {
            const invalid = Object.keys(intakeFieldErrors(this.draft.fields)).length > 0;
            this.status = invalid ? "error" : "saved";
            this.error = invalid ? "Revisa los kilómetros y el precio. Los demás datos están guardados." : null;
            this.notify();
            return !invalid;
          }
          this.draft = { ...this.draft, pending: {
            method: this.draft.vehicleId ? "PATCH" : "POST",
            path: this.draft.vehicleId ? `/vehicles/${this.draft.vehicleId}` : "/vehicles",
            body, key: crypto.randomUUID(), startedAt: new Date().toISOString(),
          } };
          this.save();
          await this.persisted();
        }
        const request = this.draft.pending;
        if (!request) throw new Error("No se ha podido recuperar el guardado pendiente.");
        // El middleware conserva respuestas 7 días. No repetir un alta ambigua caducada.
        if (Date.now() - Date.parse(request.startedAt) > 6 * 86_400_000) {
          throw new Error("Este guardado es antiguo. Comprueba primero en Vehículos si la ficha ya existe antes de continuar.");
        }
        this.status = "saving";
        this.notify();
        let result: { id: string };
        try {
          result = await this.io.send(request);
        } catch (error) {
          // Un rechazo explícito no pudo escribir. La próxima edición puede corregirlo.
          const status = error instanceof Error ? Reflect.get(error, "status") : undefined;
          if ([400, 403, 404, 409, 422].includes(status)) {
            this.draft = { ...this.draft, pending: null };
            this.save();
            await this.persisted();
          }
          throw error;
        }
        if (!result.id) throw new Error("No se ha podido confirmar el guardado. Vuelve a intentarlo.");
        this.draft = {
          ...this.draft, vehicleId: result.id, pending: null,
          synced: { ...this.draft.synced, ...request.body },
        };
        this.save();
        await this.persisted();
        // Solo envía diferencias: no pisa campos completados desde otra sección.
      }
    } catch (error) {
      this.status = "error";
      if (!this.localError) this.error = error instanceof TypeError || (error instanceof Error && error.name === "TimeoutError")
        ? "Sin conexión con el servidor. Tus datos se conservan en este dispositivo; vuelve a intentarlo al recuperar conexión."
        : error instanceof Error ? error.message : "No se ha podido guardar. Vuelve a intentarlo.";
      this.notify();
      return false;
    }
  }
}
